import React, { useEffect, useState } from 'react';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import { TeamService } from '../../services/team.service';
import { TeamMember, TeamInvitation, Team } from '../../types/team.types';
import ActiveUsersList from '../../components/team/ActiveUsersList';
import PendingUsersList from '../../components/team/PendingUsersList';
import { toast } from 'react-hot-toast';
import { BriefcaseIcon, PlusIcon, ShieldCheckIcon, UsersIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Role } from '../../types/team.types';
import { useAppSelector } from '../../hooks/useRedux';
import InviteMemberModal from '../../components/team/InviteMemberModal';
import { useNavigate, useParams } from 'react-router-dom';
import { hasPermission } from '../../utils/permissionUtils';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useVault } from '../../contexts/VaultContext';
import HeroBanner from '../../components/common/HeroBanner';

const TeamMembersPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('active');
    const [activeMembers, setActiveMembers] = useState<TeamMember[]>([]);
    const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const navigate = useNavigate();
    const { permissions, team_id, user, role_id } = useAppSelector((state) => state.auth);
    const hasInviteMembersPermission = hasPermission(permissions, 'team', 'invite_members');
    const { teamId } = useParams<{ teamId: string }>();
    const [showRoleConfirmModal, setShowRoleConfirmModal] = useState(false);
    const [selectedRoleUpdate, setSelectedRoleUpdate] = useState<{
        userId: string;
        newRoleId: number;
        currentRole?: string;
        newRole?: string;
    } | null>(null);
    const [isUpdatingRole, setIsUpdatingRole] = useState(false);
    const { searchQuery } = useVault();
    const [team, setTeam] = useState<Team | null>(null);
    const fetchTeamData = async () => {
        try {
            setIsLoading(true);
            const membersResponse = await TeamService.getTeamMembers(team_id || '');
            console.log("membersResponse", membersResponse);
            setActiveMembers(membersResponse.activeMembers || []);
            setPendingInvitations(membersResponse.pendingInvitations || []);
            setRoles(membersResponse.roles || []);
            setTeam(membersResponse.team || null);
        } catch (error) {
            console.error('Error fetching team data:', error);
            toast.error('Failed to load team data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        console.log(permissions, user);
        fetchTeamData();
    }, [team_id]);

    const handleRoleChange = async (userId: string, newRoleId: number) => {
        const currentRole = roles.find(r => r.id === activeMembers.find(m => m.id === userId)?.role_id)?.name;
        const newRole = roles.find(r => r.id === newRoleId)?.name;

        setSelectedRoleUpdate({
            userId,
            newRoleId,
            currentRole,
            newRole
        });
        setShowRoleConfirmModal(true);
    };

    const confirmRoleUpdate = async () => {
        if (!selectedRoleUpdate) return;

        try {
            setIsUpdatingRole(true);
            // Check if the member is a guest user
            const member = activeMembers.find(m => m.user_id === selectedRoleUpdate.userId);
            const isGuestUser = member?.team_id !== team_id;
            
            const response = await TeamService.updateUserRole(
                selectedRoleUpdate.userId,
                selectedRoleUpdate.newRoleId,
                team_id || '',
                isGuestUser
            );

            if (response.success) {
                toast.success('Role updated successfully');
                fetchTeamData();
            } else {
                toast.error('Failed to update role: ' + response.message);
            }
        } catch (error) {
            console.error('Error updating role:', error);
            toast.error('Error updating role');
        } finally {
            setIsUpdatingRole(false);
            setShowRoleConfirmModal(false);
            setSelectedRoleUpdate(null);
        }
    };

    const handleInviteMember = async (email: string, roleId: number) => {
        if (!team_id) return;

        try {
            await TeamService.inviteUser(email, roleId, team_id, teamId);
            fetchTeamData();
            setShowInviteForm(false);
            toast.success('Invitation sent successfully');
        } catch (error: any) {
            console.error('Error inviting member:', error);
            throw error;
        }
    };

    const handleNavigateToTeams = () => {
        navigate('/teams');
    };

    const handleNavigateToRoles = () => {
        navigate('/roles');
    };

    // Get paginated data
    const getPaginatedData = <T extends any>(data: T[]): T[] => {
        const filteredData = searchQuery
            ? data.filter((item: any) => 
                item.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.name?.toLowerCase().includes(searchQuery.toLowerCase())
              )
            : data;
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    };

    // Calculate total pages
    const getTotalPages = (totalItems: number): number => {
        const filteredCount = searchQuery
            ? (activeTab === 'active' 
                ? activeMembers.filter(member => 
                    member.email.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length
                : pendingInvitations.filter(invitation => 
                    invitation.email.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length)
            : totalItems;
        return Math.ceil(filteredCount / itemsPerPage);
    };

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);

    // Reset pagination when search query changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Pagination Component
    const Pagination = ({ totalItems }: { totalItems: number }) => {
        const totalPages = getTotalPages(totalItems);

        return (
            <div className="flex items-center justify-center px-4 py-3 mt-1 border-gray-200 sm:px-6">
                <div className="flex justify-center flex-1">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <div className="mx-2">
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i + 1}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="h-screen flex overflow-hidden bg-gray-100">
            <VaultSidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />
                <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner */}
                    <HeroBanner
                        title="Team Members"
                        description="Manage your team members and their roles"
                        icon={<UserGroupIcon className="w-8 h-8 text-white" />}
                        variant="teams"
                    >
                        {/* Tab Navigation */}
                        <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-1.5 shadow-lg border border-white/30 overflow-x-auto">
                            {hasPermission(permissions, 'team', 'view_teams') && (
                                <button
                                    onClick={() => handleNavigateToTeams()}
                                    className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                                        activeTab === 'teams'
                                            ? 'bg-white text-primary-600 shadow-lg'
                                            : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                >
                                    <BriefcaseIcon className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">Teams</span>
                                </button>
                            )}
                            <button
                                onClick={() => setActiveTab('active')}
                                className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                                    activeTab === 'active'
                                        ? 'bg-white text-primary-600 shadow-lg'
                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                }`}
                            >
                                <UsersIcon className="h-5 w-5 sm:mr-2" />
                                <span className="hidden sm:inline">Active Members</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('pending')}
                                className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                                    activeTab === 'pending'
                                        ? 'bg-white text-primary-600 shadow-lg'
                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                }`}
                            >
                                {/* Use EnvelopeIcon for pending invitations */}
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5H4.5a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-.659 1.591l-7.09 7.09a2.25 2.25 0 01-3.182 0l-7.09-7.09A2.25 2.25 0 012.25 6.993V6.75" />
                                </svg>
                                <span className="hidden sm:inline">Pending ({pendingInvitations.length})</span>
                            </button>
                            {hasPermission(permissions, 'role_management', 'view_roles') && (
                                <button
                                    onClick={() => handleNavigateToRoles()}
                                    className={`flex items-center px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                                        activeTab === 'roles'
                                            ? 'bg-white text-primary-600 shadow-lg'
                                            : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                >
                                    <ShieldCheckIcon className="h-5 w-5 sm:mr-2" />
                                    <span className="hidden sm:inline">Roles</span>
                                </button>
                            )}
                        </div>
                    </HeroBanner>

                    {/* Content Section */}
                    <div className="px-6 py-8">
                        <div className="w-full">
                            {isLoading ? (
                                <div className="mt-6 text-center">
                                    <div className="animate-pulse flex space-x-4">
                                        <div className="flex-1 space-y-6 py-1">
                                            <div className="h-40 bg-gray-200 rounded"></div>
                                            <div className="space-y-3">
                                                <div className="h-4 bg-gray-200 rounded"></div>
                                                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-6">
                                    {activeTab === 'active' ? (
                                        <>
                                            <ActiveUsersList
                                                members={getPaginatedData(activeMembers)}
                                                roles={roles}
                                                onRoleChange={handleRoleChange}
                                                onRevoke={fetchTeamData}
                                            />
                                            <Pagination totalItems={activeMembers.length} />
                                        </>
                                    ) : activeTab === 'pending' && (
                                        <>
                                            <PendingUsersList
                                                invitations={getPaginatedData(pendingInvitations)}
                                                onRevoke={fetchTeamData}
                                                onOpenInviteModal={() => setShowInviteForm(true)}
                                            />
                                            <Pagination totalItems={pendingInvitations.length} />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Floating Action Button */}
                    {hasInviteMembersPermission && role_id !== 1 && (
                        <button
                            onClick={() => setShowInviteForm(true)}
                            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 sm:p-4 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center z-10"
                            aria-label="Invite Member"
                        >
                            <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                            <span className="ml-2 hidden sm:inline">Invite Member</span>
                        </button>
                    )}
                </main>
            </div>
            {showInviteForm && (
                <InviteMemberModal
                    teamId={team_id || ''}
                    team_id={team_id || ''}
                    team={team || null}
                    onClose={() => setShowInviteForm(false)}
                    onInvite={handleInviteMember}
                />
            )}
            {showRoleConfirmModal && selectedRoleUpdate && (
                <ConfirmModal
                    isOpen={showRoleConfirmModal}
                    title="Update Role"
                    message={`Are you sure you want to change this user's role to "${selectedRoleUpdate.newRole}"?`}
                    confirmText="Update"
                    cancelButtonText="Cancel"
                    isLoading={isUpdatingRole}
                    onConfirm={confirmRoleUpdate}
                    onCancel={() => {
                        setShowRoleConfirmModal(false);
                        setSelectedRoleUpdate(null);
                    }}
                />
            )}
        </div>
    );
};

export default TeamMembersPage;